import { createRouter, createWebHistory } from 'vue-router';
import { useSessionStore } from '@/stores/session';
import { isAllowed, lockRedirect, routeForStatus } from './guard';

/** HTML5 history is required: Telegram passes launch parameters in the URL hash. */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/loading', name: 'loading', component: () => import('@/views/LoadingView.vue') },
    { path: '/language', name: 'language', component: () => import('@/views/LanguageView.vue') },
    { path: '/email', name: 'email', component: () => import('@/views/EmailView.vue') },
    { path: '/code', name: 'code', component: () => import('@/views/CodeView.vue') },
    { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
    { path: '/blocked', name: 'blocked', component: () => import('@/views/BlockedView.vue') },
    {
      path: '/withdraw',
      name: 'withdraw-asset',
      component: () => import('@/views/WithdrawAssetView.vue'),
    },
    {
      path: '/withdraw/:assetCode',
      name: 'withdraw-form',
      component: () => import('@/views/WithdrawFormView.vue'),
    },
    {
      path: '/withdraw/:assetCode/review',
      name: 'withdraw-review',
      component: () => import('@/views/WithdrawReviewView.vue'),
    },
    {
      path: '/withdraw/:id/confirm',
      name: 'withdraw-code',
      component: () => import('@/views/WithdrawCodeView.vue'),
    },
    {
      path: '/operations',
      name: 'operations',
      component: () => import('@/views/OperationsView.vue'),
    },
    {
      path: '/operations/:id',
      name: 'operation',
      component: () => import('@/views/OperationView.vue'),
    },
    {
      path: '/exchange',
      name: 'exchange-asset',
      component: () => import('@/views/ExchangeAssetView.vue'),
    },
    {
      path: '/exchange/:id([0-9a-f-]{36})/details',
      name: 'exchange-details',
      component: () => import('@/views/ExchangeDetailsView.vue'),
    },
    {
      path: '/exchange/:id([0-9a-f-]{36})/review',
      name: 'exchange-review',
      component: () => import('@/views/ExchangeReviewView.vue'),
    },
    {
      path: '/exchange/:id([0-9a-f-]{36})',
      name: 'exchange-order',
      component: () => import('@/views/ExchangeOrderView.vue'),
    },
    {
      path: '/exchange/:assetCode',
      name: 'exchange-direction',
      component: () => import('@/views/ExchangeDirectionView.vue'),
    },
    {
      path: '/exchange/:assetCode/:toCurrencyType',
      name: 'exchange-amount',
      component: () => import('@/views/ExchangeAmountView.vue'),
    },
    { path: '/settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
    {
      path: '/settings/timezone',
      name: 'settings-timezone',
      component: () => import('@/views/TimezoneView.vue'),
    },
    { path: '/lock', name: 'lock', component: () => import('@/views/LockView.vue') },
    {
      path: '/lock/reset',
      name: 'lock-reset',
      component: () => import('@/views/PinResetView.vue'),
    },
    {
      path: '/settings/pin',
      name: 'settings-pin',
      component: () => import('@/views/PinSettingsView.vue'),
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach((to) => {
  const session = useSessionStore();
  if (to.path === '/loading') return true;
  if (!session.profile) return { path: '/loading', query: { next: to.fullPath } };
  const next = typeof to.query.next === 'string' ? to.query.next : undefined;
  // Only active users can reach the lock screens; a blocked/pending user with a PIN must see
  // their status screen, not loop.
  const lock = lockRedirect(
    session.pinLocked && session.profile.status === 'active',
    to.path,
    to.fullPath,
    next,
  );
  if (lock) return lock;
  const status = session.profile.status;
  if (!isAllowed(status, to.path)) return routeForStatus(status);
  return true;
});
